/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 29, 2026 by hoarfroster
 *
 */

import { JSX, useEffect, useRef, useState } from 'react';
import { getStorageItem, setStorageItem } from '@utils/storage.ts';
import { User } from '@models/user/user.ts';
import { Link } from 'react-router';
import I18N from '@utils/i18n.base';

const userMenuRowClass =
    'text-theme-text hover:bg-theme-hover focus-visible:bg-theme-hover flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium transition-colors focus-visible:outline-none';
const userMenuIconClass = 'material-symbols-rounded text-theme-icon text-lg!';

/**
 * User state component that displays user avatar and menu for authenticated users,
 * or sign-in link for unauthenticated users.
 *
 * @param mobile - Whether this component is rendered in mobile view
 * @param onNavigate - Optional callback function called when navigation occurs
 * @param onSignOut - Optional callback function called when user choose to sign out
 * @returns {JSX.Element} The rendered user state component
 */
export default function UserState({
    mobile = false,
    onNavigate,
    onSignOut,
}: {
    mobile?: boolean;
    onNavigate?: () => void;
    onSignOut?: () => void;
}): JSX.Element {
    const [info, setInfo] = useState<User | null>(null);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const userMenuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const ui = getStorageItem('user-info');
        if (ui) {
            try {
                setInfo(User.fromJSON(JSON.parse(ui)));
            } catch (error: unknown) {
                setStorageItem('user-info', '');
                document.cookie = 'token=';
                const msg = error instanceof Error ? error.message : String(error);
                console.warn(msg);
            }
        }
    }, []);

    // Handle clicking outside to close user menu
    useEffect(() => {
        const handleUserMenuOutside = (event: MouseEvent) => {
            if (isUserMenuOpen && userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setIsUserMenuOpen(false);
            }
        };
        if (isUserMenuOpen) document.addEventListener('mousedown', handleUserMenuOutside);
        return () => document.removeEventListener('mousedown', handleUserMenuOutside);
    }, [isUserMenuOpen]);

    const handleClick = () => {
        if (onNavigate) onNavigate();
    };

    const computedUserAvatarURL = info?.avatar
        ? `${import.meta.env.VITE_SERVER_ENDPOINT}/user/user/avatar/${info.id}`
        : '/favicon.avif';

    if (info) {
        return (
            <div className="relative" ref={userMenuRef}>
                <button
                    type="button"
                    aria-label="user-menu"
                    className="bg-theme-avatar ring-theme-border-divider flex h-8 w-8 items-center justify-center overflow-hidden rounded-full ring-1 transition-transform select-none active:scale-95"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsUserMenuOpen((s) => !s);
                    }}
                >
                    {info.avatar ? (
                        <img src={computedUserAvatarURL} alt={I18N.app.avatar} className="h-full w-full object-cover" />
                    ) : (
                        <span className="material-symbols-rounded text-theme-text">person</span>
                    )}
                </button>

                {isUserMenuOpen && (
                    <div className="bg-theme-popup-bg ring-theme-border-divider fixed top-14 right-3 z-50 w-[17rem] rounded-xl shadow-xl ring-1">
                        <div className="p-2">
                            <div className="flex items-start gap-2.5 px-1 py-1">
                                <img
                                    src={computedUserAvatarURL}
                                    alt={I18N.app.avatar}
                                    className="bg-theme-avatar h-9 w-9 rounded-full object-cover"
                                />
                                <div className="flex-1 truncate">
                                    <div className="text-theme-text truncate text-sm font-semibold">{info.name}</div>
                                    <div className="text-theme-text-muted truncate text-xs">{info.email ?? ''}</div>
                                </div>
                            </div>
                            <div className="bg-theme-border-divider my-2 h-px" />

                            <div className="flex flex-col">
                                <Link
                                    to="/settings/profile"
                                    onClick={() => {
                                        setIsUserMenuOpen(false);
                                        handleClick();
                                    }}
                                    className={userMenuRowClass}
                                >
                                    <span className={userMenuIconClass}>person</span>
                                    <span>{I18N.app.profileSettings}</span>
                                </Link>
                                <Link
                                    to="/settings/preferences"
                                    onClick={() => {
                                        setIsUserMenuOpen(false);
                                        handleClick();
                                    }}
                                    className={userMenuRowClass}
                                >
                                    <span className={userMenuIconClass}>tune</span>
                                    <span>{I18N.app.preferences}</span>
                                </Link>
                                <div className="bg-theme-border-divider my-1.5 h-px" />
                                <Link
                                    to="/settings/security"
                                    onClick={() => {
                                        setIsUserMenuOpen(false);
                                        handleClick();
                                    }}
                                    className={userMenuRowClass}
                                >
                                    <span className={userMenuIconClass}>shield</span>
                                    <span>{I18N.app.securitySettings}</span>
                                </Link>
                                <Link
                                    to="/user/credentials"
                                    onClick={() => {
                                        setIsUserMenuOpen(false);
                                        handleClick();
                                    }}
                                    className={userMenuRowClass}
                                >
                                    <span className={userMenuIconClass}>key</span>
                                    <span>{I18N.app.credentials}</span>
                                </Link>
                            </div>

                            <div className="bg-theme-border-divider my-2 h-px" />
                            <button
                                type="button"
                                onClick={() => {
                                    setIsUserMenuOpen(false);
                                    if (onSignOut) onSignOut();
                                    handleClick();
                                }}
                                className={userMenuRowClass}
                            >
                                <span className={userMenuIconClass}>logout</span>
                                <span>{I18N.auth.signOut}</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    } else {
        return (
            <Link
                to="/auth/sign-in"
                onClick={handleClick}
                className={`text-theme-text font-medium ${
                    mobile ? 'hover:bg-theme-hover flex items-center rounded-lg px-4 py-3' : ''
                }`}
            >
                {mobile ? (
                    <>
                        <span className="material-symbols-rounded mr-3 text-xl">login</span>
                        {I18N.auth.signIn}
                    </>
                ) : (
                    <>
                        {I18N.auth.signIn}
                        <span className="material-symbols-rounded ml-2 align-middle" aria-hidden="true">
                            login
                        </span>
                    </>
                )}
            </Link>
        );
    }
}
